import { isNil, path } from 'ramda'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Switch, Route } from 'react-router-dom'
import styled from 'styled-components'
import config from '@/config'
import DashboardMenu from '@/components/dashboard-menu'
import PageTitle from '@/components/page-title'
import useGetSuiteInfo from '@/hooks/get-suite-info'
import ContractHistory from '@/pages/Tenants/TenantResources/contract-history'
import ResourcesContractView from '@/pages/Tenants/TenantResources/resources-contract-view'
import useBetaUser from '@/hooks/use-beta-user'
import useCurrentSession from '@/hooks/use-current-session'
import { PaymentsDashboard, Payment, PaymentReview, PadPayment } from './Payments'
import RentalChat from './RentalChat'
import Resources from './Resources'
import { ListingDetail } from '../Listing'
import { ContractView, ContractManager } from '../Contract'

const { BUILD_ENV } = config

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const RentalDashboard = ({
  match: {
    params: { listingId }
  }
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const session = useCurrentSession()
  const contract = useSelector(path(['listing', listingId, 'contract']))
  const betaUser = useBetaUser()
  const betaUserContractLite = betaUser.custom_contract
  const isDev = BUILD_ENV === 'dev'
  const currentBetaUser = isDev ? true : betaUserContractLite.includes(parseInt(session.id))
  const hasContract = !isNil(contract)
  const contractId = contract?.listing_contract_id
  const isNative = contract?.contract_structure === 'native'
  const listingDetails = useGetSuiteInfo(listingId)
  const markedAsRented = contract?.marked_as_rented === '1'
  const activeContract =
    contract?.state_machine === 'active' || contract?.state_machine === 'signed'

  useEffect(() => {
    dispatch.listing.getContract(listingId)
  }, [dispatch.listing, listingId])

  useEffect(() => {
    dispatch.listing.load(listingId)
  }, [dispatch.listing, listingId])

  if (!listingDetails) return null

  const menuItems = [
    {
      link: `/rental/${listingId}`,
      text: t('b.rental.view.overview.link', 'Overview'),
      exact: true
    },
    {
      link: `/rental/${listingId}/chat`,
      text: t('common.chat', 'Chat')
    },
    {
      link: `/rental/${listingId}/contract/${contractId}`,
      text: t('b.rental.view.contract.link', 'Contract'),
      disabled: markedAsRented
    },
    {
      link: `/rental/${listingId}/payments/`,
      text: t('b.rental.view.payments.link', 'Payments'),
      disabled: markedAsRented
    },
    {
      link: `/rental/${listingId}/resources`,
      text: t('b.rental.view.resources.link', 'Resources'),
      disabled: markedAsRented
    }
  ]
  const ContractComponent =
    !isNative && currentBetaUser && hasContract ? ContractManager : ContractView

  return (
    <>
      <PageTitle>{listingDetails.full_street_name}</PageTitle>
      {(activeContract || markedAsRented) && <DashboardMenu menuItems={menuItems} />}
      <Wrapper>
        <Switch>
          <Route
            exact
            path="/rental/:listingId"
            render={props => <ListingDetail noTitle {...props} />}
          />
          <Route exact path="/rental/:listingId/chat" component={RentalChat} />
          <Route exact path="/rental/:listingId/chat/:timelineId" component={RentalChat} />
          <Route
            exact
            path="/rental/:listingId/contract/:contractId/:step(review|terms|sign)?"
            render={props => <ContractComponent noTitle {...props} />}
          />

          <Route exact path="/rental/:listingId/payments/:paymentId" component={Payment} />
          <Route
            exact
            path="/rental/:listingId/payments/:paymentId/pre-authorized-debit"
            component={PadPayment}
          />
          <Route
            exact
            path="/rental/:listingId/payments/:paymentId/review"
            component={PaymentReview}
          />
          <Route exact path="/rental/:listingId/payments" component={PaymentsDashboard} />
          <Route exact path="/rental/:listingId/resources" component={Resources} />
          <Route
            exact
            path="/rental/:listingId/resources/contract-history"
            component={ContractHistory}
          />
          <Route
            exact
            path="/rental/:listingId/resources/contract-history/:contractId"
            component={ResourcesContractView}
          />
        </Switch>
      </Wrapper>
    </>
  )
}

export default RentalDashboard
